import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "./Reveal";
import { ASSETS } from "../../data/content";

export const Hero = ({ scrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax leaf backdrop */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={ASSETS.heroLeaf}
          alt=""
          className="w-full h-full object-cover opacity-[0.14]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-base via-brand-base/60 to-brand-base" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-base via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24 pt-40">
        <div className="overflow-hidden mb-8">
          <motion.p
            initial={{ y: "120%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overline"
          >
            Peil Consultancy Ltd — RC 957357 · Lagos & Abuja
          </motion.p>
        </div>

        <h1 className="font-display font-light text-brand-text tracking-tighter leading-[0.92] text-[15vw] sm:text-[12vw] md:text-8xl lg:text-[8.5rem]">
          <MaskLine delay={0.45}>Human capital,</MaskLine>
          <MaskLine delay={0.6} className="text-brand italic">reimagined</MaskLine>
          <MaskLine delay={0.75}>for Africa.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-brand-soft text-base md:text-lg leading-relaxed font-light">
            A world-class partner in human capital development, capacity building
            and management — adding excellent value to our clients&apos; business for
            over four decades.
          </p>
          <button
            onClick={() => scrollTo("services")}
            data-testid="hero-explore"
            className="group flex items-center gap-3 text-brand-text hover:text-brand transition-colors duration-300 shrink-0"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Explore our practice</span>
            <span className="grid place-items-center w-11 h-11 rounded-full border border-brand-line group-hover:border-brand group-hover:rotate-45 transition-all duration-300">
              <ArrowDownRight size={18} />
            </span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
